import React, { useState, useEffect, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, X, Loader2 } from 'lucide-react'
import { Button } from '../ui/Button'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../ui/AlertDialog'
import toast from 'react-hot-toast'

const LOGO_ACCEPTED_MIME_TYPES = {
  'image/png': [],
  'image/jpeg': [],
  'image/webp': [],
  'image/svg+xml': [],
}
const LOGO_MAX_BYTES = 2 * 1024 * 1024

interface LogoDropzoneProps {
  value: File | undefined
  onChange: (file: File | undefined) => void
  existingLogoUrl?: string | null
  onDelete?: () => Promise<void>
}

export const LogoDropzone: React.FC<LogoDropzoneProps> = ({
  value,
  onChange,
  existingLogoUrl,
  onDelete,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!value) {
      setPreviewUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(value)
    setPreviewUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [value])

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (!file) return
      onChange(file)
    },
    [onChange]
  )

  const onDropRejected = useCallback(() => {
    toast.error('Logo must be PNG, JPEG, WebP, or SVG and under 2MB.')
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: LOGO_ACCEPTED_MIME_TYPES,
    maxSize: LOGO_MAX_BYTES,
    multiple: false,
    disabled: !!value || !!existingLogoUrl,
  })

  const handleRemoveClick = useCallback(() => {
    if (value) {
      onChange(undefined)
      return
    }
    if (existingLogoUrl && onDelete) {
      setConfirmOpen(true)
    }
  }, [value, existingLogoUrl, onDelete, onChange])

  const handleConfirmDelete = useCallback(async () => {
    if (!onDelete) return
    setDeleting(true)
    setConfirmOpen(false)
    try {
      await onDelete()
      toast.success('Logo deleted')
    } catch {
      toast.error('Failed to delete logo')
    } finally {
      setDeleting(false)
    }
  }, [onDelete])

  const displayUrl = previewUrl ?? existingLogoUrl ?? null
  const showingExistingOnly = !value && !!existingLogoUrl

  if (displayUrl) {
    return (
      <>
        <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
          <img
            src={displayUrl}
            alt="Logo preview"
            className="h-10 w-10 rounded object-contain border border-border bg-white"
          />
          <span className="flex-1 text-sm text-foreground truncate">
            {value ? value.name : 'Current logo'}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={showingExistingOnly ? 'Delete saved logo' : 'Remove selected logo'}
            onClick={handleRemoveClick}
            disabled={deleting}
            className="shrink-0 h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
          >
            {deleting
              ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              : <X className="h-4 w-4" aria-hidden="true" />
            }
          </Button>
        </div>

        <AlertDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete logo?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently remove the saved logo. You can upload a new one at any time.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmOpen(false)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={handleConfirmDelete}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    )
  }

  return (
    <div
      {...getRootProps()}
      role="button"
      aria-label="Upload logo file"
      className={[
        'flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed p-5 cursor-pointer transition-colors',
        isDragActive
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50',
      ].join(' ')}
    >
      <input {...getInputProps()} />
      <Upload className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
      <p className="text-sm text-muted-foreground text-center">
        {isDragActive ? 'Drop logo here' : 'Drop a file or click to upload'}
      </p>
      <p className="text-xs text-muted-foreground/70">PNG, JPEG, WebP, SVG · max 2MB</p>
    </div>
  )
}
